/**
 * UnifiedFoodCardModal — THE single entry point for all FoodCard creation
 * 
 * Triggered from: Bottom nav Post, Feed CTAs, Quest Verify, Repost, anywhere
 * Context-aware: Quest verification, general post, repost
 * Gemini AI: Dish identification, caption, hashtags, quality check, quest matching
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Camera, Upload, Sparkles, Loader, X, MapPin, Check,
  Hash, ChefHat, Send, Image as ImageIcon, RefreshCw, Zap, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Props:
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {object} questContext - { quest_id, quest_title, quest_blurb } if from quest verification
 * @param {object} prefill - { name, cuisine, venue_name, image_url } for repost/remix
 * @param {string} mode - 'post' | 'quest_verify' | 'repost'
 */
const UnifiedFoodCardModal = ({ isOpen, onClose, questContext = null, prefill = null, mode = 'post' }) => {
  const { user } = useAuth();
  const { locationString } = useLocation();
  const { toast } = useToast();
  const fileRef = useRef(null);

  const [stage, setStage] = useState('upload'); // upload | analyzing | preview | publishing | success
  const [imagePreview, setImagePreview] = useState(prefill?.image_url || null);
  const [imageFile, setImageFile] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(prefill?.image_url || null);

  // AI results
  const [dishName, setDishName] = useState(prefill?.name || '');
  const [cuisine, setCuisine] = useState(prefill?.cuisine || '');
  const [description, setDescription] = useState('');
  const [venueName, setVenueName] = useState(prefill?.venue_name || '');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [dietaryTags, setDietaryTags] = useState([]);
  const [confidence, setConfidence] = useState(0);
  const [questMatch, setQuestMatch] = useState(null);
  const [qualityWarning, setQualityWarning] = useState('');

  const token = localStorage.getItem('fq_auth_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStage(prefill?.image_url ? 'preview' : 'upload');
      setImagePreview(prefill?.image_url || null);
      setUploadedUrl(prefill?.image_url || null);
      setDishName(prefill?.name || '');
      setCuisine(prefill?.cuisine || '');
      setVenueName(prefill?.venue_name || '');
      setDescription('');
      setCaption('');
      setHashtags([]);
      setQuestMatch(null);
      setQualityWarning('');
    }
  }, [isOpen, prefill]);

  const handleFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast({ title: 'Please select an image or video', variant: 'destructive' });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: 'File too large (max 15MB)', variant: 'destructive' });
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  }, [toast]);

  const uploadImage = async () => {
    if (!imageFile) return uploadedUrl;
    const fd = new FormData();
    fd.append('file', imageFile);
    try {
      const res = await fetch(`${API_URL}/api/media/upload`, { method: 'POST', credentials: 'include', headers, body: fd });
      if (res.ok) { const d = await res.json(); return d.url; }
    } catch {}
    return null;
  };

  const analyzeWithAI = async () => {
    if (!imagePreview) return;
    setStage('analyzing');
    try {
      let imgUrl = uploadedUrl;
      if (!imgUrl && imageFile) { imgUrl = await uploadImage(); setUploadedUrl(imgUrl); }

      const base64 = imagePreview.includes(',') ? imagePreview.split(',')[1] : null;
      const res = await fetch(`${API_URL}/api/foodcard/analyze-unified`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        credentials: 'include',
        body: JSON.stringify({
          image_base64: base64,
          image_url: imgUrl,
          location: locationString,
          venue_name: venueName,
          quest_context: questContext ? { quest_id: questContext.quest_id, title: questContext.quest_title, blurb: questContext.quest_blurb } : null,
          mode,
        }),
      });
      if (!res.ok) throw new Error('Analysis failed');
      const data = await res.json();

      if (data.quality_warning) setQualityWarning(data.quality_warning);
      setDishName(data.dish_name || dishName || 'Delicious Dish');
      setCuisine(data.cuisine || cuisine || '');
      setDescription(data.description || '');
      setCaption(data.share_caption || data.suggested_caption || '');
      setHashtags(data.suggested_hashtags || data.hashtags || ['#FoodQuest']);
      setDietaryTags(data.dietary_tags || []);
      setConfidence(data.confidence || 0);
      if (data.quest_match !== undefined) setQuestMatch(data.quest_match);
      setStage('preview');
    } catch {
      toast({ title: 'AI analysis failed', description: 'Try a clearer photo or continue manually', variant: 'destructive' });
      setStage('preview'); // Allow manual fill
    }
  };

  const handlePublish = async () => {
    if (!user) { toast({ title: 'Sign in to post', variant: 'destructive' }); return; }
    setStage('publishing');
    try {
      let imgUrl = uploadedUrl;
      if (!imgUrl && imageFile) { imgUrl = await uploadImage(); setUploadedUrl(imgUrl); }

      const fd = new FormData();
      fd.append('dish_name', dishName);
      fd.append('description', description || caption);
      fd.append('cuisine', cuisine);
      fd.append('venue_name', venueName);
      fd.append('location', locationString || '');
      fd.append('vibe', '');
      fd.append('hashtags', hashtags.join(','));
      if (imgUrl) fd.append('image_url', imgUrl);
      if (questContext?.quest_id) fd.append('quest_id', questContext.quest_id);

      const res = await fetch(`${API_URL}/api/foodcard/create`, { method: 'POST', credentials: 'include', headers, body: fd });
      if (!res.ok) throw new Error('Publish failed');

      // If quest verification, also submit quest response
      if (questContext?.quest_id && mode === 'quest_verify') {
        await fetch(`${API_URL}/api/quests/respond`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          credentials: 'include',
          body: JSON.stringify({ quest_id: questContext.quest_id, action: 'verify', photo_url: imgUrl, notes: caption }),
        });
      }

      setStage('success');
      toast({ title: questContext ? 'Quest Verified & Posted!' : 'FoodCard Posted!' });
      setTimeout(() => { onClose?.(); }, 1500);
    } catch {
      toast({ title: 'Failed to publish', variant: 'destructive' });
      setStage('preview');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-end md:items-center justify-center" data-testid="unified-foodcard-modal">
      <div className="bg-white w-full max-w-md rounded-t-[28px] md:rounded-[28px] shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-5 py-3 border-b border-gray-100 rounded-t-[28px]">
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-gray-900">
              {mode === 'quest_verify' ? 'Verify Quest' : mode === 'repost' ? 'Repost FoodCard' : 'New FoodCard'}
            </h2>
            {questContext && <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">Quest</span>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        {/* Quest context banner */}
        {questContext && stage !== 'success' && (
          <div className="mx-5 mt-3 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
            <p className="text-xs font-bold text-emerald-800 mb-0.5">Verifying: {questContext.quest_title}</p>
            <p className="text-[10px] text-emerald-600 line-clamp-1">{questContext.quest_blurb}</p>
          </div>
        )}

        {/* Upload stage */}
        {(stage === 'upload' || (stage === 'preview' && !imagePreview)) && (
          <div className="p-5">
            <button onClick={() => fileRef.current?.click()}
              className="w-full aspect-[4/3] bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-3 hover:bg-gray-100 hover:border-[#FF6B35]/30 transition-all">
              <div className="w-16 h-16 bg-[#FFF5F0] rounded-2xl flex items-center justify-center">
                <Camera className="w-8 h-8 text-[#FF6B35]" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-700">Tap to snap or upload</p>
                <p className="text-xs text-gray-400 mt-0.5">Photo or video (max 15MB)</p>
              </div>
            </button>
            <input ref={fileRef} type="file" accept="image/*,video/*" capture="environment" style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }} onChange={handleFile} />

            {imagePreview && (
              <div className="mt-4">
                <div className="relative rounded-2xl overflow-hidden">
                  <img src={imagePreview} alt="Preview" className="w-full aspect-[4/3] object-cover" />
                  <button onClick={() => { setImagePreview(null); setImageFile(null); }}
                    className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full text-white"><X className="w-4 h-4" /></button>
                </div>
                <button onClick={analyzeWithAI}
                  className="w-full mt-3 py-3 bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white font-bold text-sm rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98]"
                  data-testid="ai-identify-btn">
                  <Sparkles className="w-4 h-4" />Let AI Identify This Dish
                </button>
                <button onClick={() => setStage('preview')}
                  className="w-full mt-2 py-2.5 text-gray-500 text-sm font-medium hover:text-gray-700">
                  Skip AI — fill manually
                </button>
              </div>
            )}
          </div>
        )}

        {/* Analyzing stage */}
        {stage === 'analyzing' && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-[#FFF5F0] rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Sparkles className="w-8 h-8 text-[#FF6B35]" />
            </div>
            <p className="text-base font-bold text-gray-900 mb-1">Identifying your dish...</p>
            <p className="text-sm text-gray-500">Gemini AI is analyzing the photo</p>
            <div className="w-32 h-1 bg-gray-100 rounded-full mx-auto mt-4 overflow-hidden">
              <div className="h-full bg-[#FF6B35] rounded-full animate-pulse" style={{ width: '60%' }} />
            </div>
          </div>
        )}

        {/* Preview / Edit stage */}
        {stage === 'preview' && imagePreview && (
          <div className="p-5 space-y-4">
            {/* Image preview */}
            <div className="relative rounded-2xl overflow-hidden">
              <img src={imagePreview} alt="Preview" className="w-full aspect-[4/3] object-cover" />
              {confidence > 0 && (
                <span className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${confidence >= 80 ? 'bg-emerald-500 text-white' : confidence >= 50 ? 'bg-amber-400 text-gray-900' : 'bg-red-500 text-white'}`}>
                  {confidence}% match
                </span>
              )}
              {questMatch === true && (
                <span className="absolute top-2 right-2 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                  <Check className="w-3 h-3" />Quest Complete!
                </span>
              )}
              <button onClick={() => fileRef.current?.click()}
                className="absolute bottom-2 right-2 p-2 bg-black/50 rounded-full text-white"><RefreshCw className="w-4 h-4" /></button>
            </div>
            <input ref={fileRef} type="file" accept="image/*,video/*" capture="environment" style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }} onChange={handleFile} />

            {/* Quality warning */}
            {qualityWarning && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />{qualityWarning}
              </div>
            )}

            {/* Dish name */}
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Dish Name</label>
              <input value={dishName} onChange={e => setDishName(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20" placeholder="What did you eat?" />
            </div>

            {/* Cuisine + Venue */}
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Cuisine</label>
                <input value={cuisine} onChange={e => setCuisine(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20" placeholder="Italian, Japanese..." />
              </div>
              <div className="flex-1">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Restaurant</label>
                <input value={venueName} onChange={e => setVenueName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20" placeholder="Where?" />
              </div>
            </div>

            {/* Location */}
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <MapPin className="w-3.5 h-3.5 text-[#FF6B35]" />
              <span>{locationString || 'Location not set'}</span>
            </div>

            {/* AI-generated caption */}
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Caption</label>
              <textarea value={caption} onChange={e => setCaption(e.target.value)} rows={2}
                className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20" placeholder="Add a witty caption..." />
            </div>

            {/* Dietary tags */}
            {dietaryTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {dietaryTags.map(t => <span key={t} className="bg-emerald-50 text-emerald-700 text-[10px] font-medium px-2 py-0.5 rounded-full">{t}</span>)}
              </div>
            )}

            {/* Hashtags */}
            <div className="flex flex-wrap gap-1.5">
              {hashtags.map((tag, i) => (
                <span key={i} className="bg-[#FFF5F0] text-[#FF6B35] text-xs font-medium px-2.5 py-1 rounded-full">#{tag.replace('#', '')}</span>
              ))}
            </div>

            {/* Re-analyze button */}
            <button onClick={analyzeWithAI}
              className="w-full py-2 text-[#FF6B35] text-xs font-semibold flex items-center justify-center gap-1 hover:bg-[#FFF5F0] rounded-xl transition-colors">
              <Sparkles className="w-3.5 h-3.5" />Re-analyze with AI
            </button>

            {/* Publish */}
            <button onClick={handlePublish}
              className="w-full py-3.5 bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white font-bold text-sm rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] shadow-lg"
              data-testid="publish-foodcard-btn">
              <Send className="w-4 h-4" />
              {mode === 'quest_verify' ? 'Verify & Post' : 'Post FoodCard'}
            </button>
          </div>
        )}

        {/* Publishing */}
        {stage === 'publishing' && (
          <div className="p-8 text-center">
            <Loader className="w-10 h-10 text-[#FF6B35] animate-spin mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">Publishing your FoodCard...</p>
          </div>
        )}

        {/* Success */}
        {stage === 'success' && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-base font-bold text-gray-900 mb-1">
              {mode === 'quest_verify' ? 'Quest Verified!' : 'FoodCard Live!'}
            </p>
            <p className="text-sm text-gray-500">Your post is now in the Foodie Feed</p>
          </div>
        )}

        {/* AI disclosure */}
        {stage !== 'upload' && (
          <p className="text-[9px] text-gray-400 text-center px-5 pb-3">
            AI-suggested details may vary. Always verify with your own experience.
          </p>
        )}
      </div>
    </div>
  );
};

export default UnifiedFoodCardModal;
